import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import {
  Shield, ShieldCheck, Activity, FileText, AlertTriangle,
  LogOut, Menu, X, Users, BarChart3, Globe, Bug, Accessibility,
  History, Package, Zap, Eye, Settings, Lock, UserCog, Mail
} from 'lucide-react';
import './App.css';

// Import client auth components
import ClientLoginPage from './components/auth/ClientLoginPage';
import ClientPortal from './components/auth/ClientPortal';
import { ClientAuthProvider } from './context/ClientAuthContext';
import { PublicPoliciesListPage, PublicPolicyDetailPage } from './pages/PublicPolicyPage';

// Import page components
import PentestPage from './pages/PentestPage';
import AccessibilityPage from './pages/AccessibilityPage';
import ControlsPage from './pages/ControlsPage';
import PoliciesPage from './pages/PoliciesPage';
import FindingsPage from './pages/FindingsPage';
import MonitorsPage from './pages/MonitorsPage';
import CAIQPage from './pages/CAIQPage';
import EvidencePage from './pages/EvidencePage';
import ReportsPage from './pages/ReportsPage';
import AuditTrailPage from './pages/AuditTrailPage';
import TrustCenterPreview from './pages/TrustCenterPreview';
import TrustCenterPage from './pages/TrustCenterPage';
import SecurityOverviewPage from './pages/SecurityOverviewPage';
import ClientsPage from './pages/ClientsPage';
import AuditPage from './pages/AuditPage';
import RosterPage from './pages/RosterPage';
import UserManagementPage from './pages/UserManagementPage';
import TrustRequestsPage from './pages/TrustRequestsPage';

// Import widgets
import SecurityScanningWidget from './components/widgets/SecurityScanningWidget';
import AccessibilityWidget from './components/widgets/AccessibilityWidget';

// Import shared UI
import { ProgressRing, Badge, Card, StatCard, CATEGORIES } from './components/ui-custom/ComplianceUI';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const STATUS_COLORS = {
  passing: "#059669", failing: "#dc2626", warning: "#d97706",
  not_implemented: "#6b7280", not_applicable: "#9ca3af", manual_review: "#7c3aed",
};

// ============ Security: Disable right-click and dev tools ============
if (typeof window !== 'undefined') {
  // Disable right-click context menu
  document.addEventListener('contextmenu', (e) => e.preventDefault());
  
  // Disable common keyboard shortcuts for dev tools
  document.addEventListener('keydown', (e) => {
    // F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
    if (
      e.key === 'F12' ||
      (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) ||
      (e.ctrlKey && e.shiftKey && (e.key === 'J' || e.key === 'j')) ||
      (e.ctrlKey && (e.key === 'U' || e.key === 'u'))
    ) {
      e.preventDefault();
    }
  });
}

// ============ Auth Context ============
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

// =====================================================================
// Token storage (cross-origin safe)
//
// Production has the frontend on `threebiecompliance.com` and the backend
// on `app-migration-73.emergent.host` — two different origins. Cookies set
// by the backend are therefore "third-party" and Chrome blocks them in
// incognito and intermittently in normal mode (manifests as a login loop).
//
// To make auth reliable cross-origin we ALSO send the JWT in an
// `Authorization: Bearer <token>` header on every request. The backend
// already supports both methods, so cookies still work on same-origin
// preview deployments.
// =====================================================================
const TOKEN_KEY = 'threebie_auth_token';

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function setAuthToken(token) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    /* storage disabled — sessionStorage will lapse with the tab anyway */
  }
}

/**
 * authFetch — same shape as fetch() but automatically attaches the bearer
 * token. Use this for every API call that needs the user's session.
 *
 * It also keeps `credentials: 'include'` so same-origin cookie-based auth
 * still works on preview/local environments.
 */
export function authFetch(url, options = {}) {
  const token = getAuthToken();
  const headers = new Headers(options.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(url, { credentials: 'include', ...options, headers });
}

// Monkey-patch global fetch so any legacy `fetch()` call across the codebase
// also gets the bearer token. This is defensive — we keep `authFetch` as the
// preferred API, but the patch ensures none of the ~200 fetch() call sites
// silently drop auth on cross-origin production.
//
// Exception: public endpoints under `/api/public/*` are read-only and must
// remain truly anonymous. Attaching a stale/expired bearer token there has
// caused public pages (e.g. /privacy) to receive unexpected 401s from the
// auth middleware. We deliberately bypass the bearer injection for those
// paths.
if (typeof window !== 'undefined' && !window.__threebieFetchPatched) {
  const _originalFetch = window.fetch.bind(window);
  // Expose the untouched fetch for pages that need guaranteed-anonymous
  // reads (public policies, privacy page, trust-center system status).
  window.__originalFetch = _originalFetch;

  const _isPublicUrl = (input) => {
    try {
      const url = typeof input === 'string' ? input : (input?.url || '');
      return /\/api\/public\//.test(url);
    } catch {
      return false;
    }
  };

  window.fetch = (input, init = {}) => {
    // Public endpoints → use the original fetch, no credentials, no auth.
    if (_isPublicUrl(input)) {
      const cleanInit = { ...init };
      delete cleanInit.credentials;
      return _originalFetch(input, cleanInit);
    }

    const token = getAuthToken();
    if (!token) return _originalFetch(input, init);
    const headers = new Headers(init.headers || {});
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return _originalFetch(input, { credentials: 'include', ...init, headers });
  };
  window.__threebieFetchPatched = true;
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleConfig, setGoogleConfig] = useState(null);
  const [permissions, setPermissions] = useState({}); // resource_key → level
  const [rbacRole, setRbacRole] = useState(null);     // owner | admin | user

  // Fetch Google OAuth config from backend on mount
  useEffect(() => {
    const fetchGoogleConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/google/config`);
        if (response.ok) {
          const config = await response.json();
          setGoogleConfig(config);
        }
      } catch (error) {
        console.error('Failed to fetch Google config:', error);
      }
    };
    fetchGoogleConfig();
  }, []);

  const checkAuth = useCallback(async () => {
    // Fetch current user + RBAC permissions. Works on every route —
    // including /auth/callback where the OAuth handler explicitly calls
    // it after the cookie / bearer-token is in place.
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',  // Send httpOnly cookies (same-origin only)
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);

        // Fetch RBAC permissions alongside user info
        try {
          const permRes = await fetch(`${API_URL}/api/users/me/permissions`, {
            credentials: 'include',
          });
          if (permRes.ok) {
            const pData = await permRes.json();
            setPermissions(pData.permissions || {});
            setRbacRole(pData.role || null);
          }
        } catch (permErr) {
          console.warn('Could not fetch permissions:', permErr);
        }
      } else if (response.status === 401) {
        // Token is no longer valid (expired / revoked / cleared on server).
        // Drop our local copy so the next sign-in starts fresh.
        setAuthToken(null);
        setUser(null);
        setPermissions({});
        setRbacRole(null);
      }
      // Don't clear anything on other failures - cookies are managed by server
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // Direct Google OAuth - no Emergent SSO
    if (!googleConfig) {
      console.error('Google OAuth config not loaded yet');
      return;
    }
    
    const params = new URLSearchParams({
      client_id: googleConfig.client_id,
      redirect_uri: window.location.origin + '/auth/callback',
      response_type: 'code',
      scope: googleConfig.scopes.join(' '),
      access_type: 'offline',
      prompt: 'select_account'
    });
    
    window.location.href = `${googleConfig.auth_endpoint}?${params.toString()}`;
  };

  const logout = async () => {
    // SECURITY: Server clears the httpOnly cookie
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear the bearer token on this device
    setAuthToken(null);
    // Also clear any stale session cookie
    document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, login, logout, checkAuth, googleConfig,
      permissions, rbacRole,
      hasPermission: (resource, required = 'read') => {
        if (!resource) return true;
        // If RBAC role hasn't loaded yet, be permissive — the backend will
        // still enforce access. This avoids the sidebar going blank on the
        // very first render or on deployments where the permissions
        // endpoint isn't available yet.
        if (!rbacRole || Object.keys(permissions).length === 0) return true;
        const order = { none: 0, read: 1, write: 2, delete: 3 };
        return (order[permissions[resource]] ?? 0) >= (order[required] ?? 0);
      },
      canRead: (r) => {
        if (!r) return true;
        if (!rbacRole || Object.keys(permissions).length === 0) return true;
        const order = { none: 0, read: 1, write: 2, delete: 3 };
        return (order[permissions[r]] ?? 0) >= 1;
      },
      canWrite: (r) => {
        if (!r) return true;
        if (!rbacRole || Object.keys(permissions).length === 0) return true;
        const order = { none: 0, read: 1, write: 2, delete: 3 };
        return (order[permissions[r]] ?? 0) >= 2;
      },
      canDelete: (r) => {
        if (!r) return true;
        if (!rbacRole || Object.keys(permissions).length === 0) return true;
        const order = { none: 0, read: 1, write: 2, delete: 3 };
        return (order[permissions[r]] ?? 0) >= 3;
      },
      isOwner: rbacRole === 'owner',
      isAdminOrOwner: rbacRole === 'owner' || rbacRole === 'admin',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ============ Auth Callback (Legacy SSO) ============
function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      
      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: 'include',
        });

        if (response.ok) {
          const userData = await response.json();
          // SECURITY: Session token is set as httpOnly cookie by server
          // Do NOT store in localStorage (XSS risk)
          setUser(userData);
          navigate('/dashboard', { replace: true });
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg = errorData.detail || `Authentication failed (${response.status})`;
          toast.error(errorMsg);
          navigate('/login');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication error');
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="threebie-loading">
      <div className="spinner" />
      <p>Authenticating...</p>
    </div>
  );
}

// ============ Auth Callback (Direct Google OAuth) ============
function AuthCallbackPage() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      
      if (error) {
        toast.error(`Google login failed: ${error}`);
        navigate('/login');
        return;
      }

      if (!code) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/google/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            code: code,
            redirect_uri: window.location.origin + '/auth/callback'
          })
        });

        if (response.ok) {
          const data = await response.json();
          // Save the access_token for use as Authorization: Bearer on every
          // subsequent API call. This is what makes auth survive cross-origin
          // production (where third-party cookies are blocked in incognito).
          if (data.access_token) {
            setAuthToken(data.access_token);
          }
          // Trust the user payload returned by the callback so we don't
          // race the cookie/token commit with an immediate /me re-fetch.
          setUser(data.user);
          // Re-run checkAuth to also load RBAC permissions.
          await checkAuth();
          navigate('/dashboard', { replace: true });
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg = errorData.detail || `Authentication failed (${response.status})`;
          toast.error(errorMsg);
          navigate('/login');
        }
      } catch (error) {
        console.error('Google auth callback error:', error);
        toast.error('Authentication error');
        navigate('/login');
      }
    };

    processGoogleAuth();
  }, [navigate, setUser, checkAuth]);

  return (
    <div className="threebie-loading">
      <div className="spinner" />
      <p>Authenticating with Google...</p>
    </div>
  );
}

// ============ Protected Route ============
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="threebie-loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// ============ Login Page ============
function LoginPage() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // If already authenticated, jump to dashboard. We do NOT auto-redirect
  // to Google — that created an infinite loop on production whenever Google
  // sent the user back without completing (e.g. they clicked "back to login"
  // on the account picker, OAuth state was dropped, or the consent was
  // cancelled). The user must click "Sign in with Google" intentionally.
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  // Surface OAuth errors that came back on the URL (e.g. ?error=access_denied
  // or our backend's ?error=Authentication%20failed)
  const oauthError = searchParams.get('error');

  return (
    <div className="login-page">
      <div className="login-bg" />
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <img src="/threebie-logo.svg" alt="Threebie" className="threebie-logo-img" />
          </div>
          <p className="login-subtitle">Consumer Fintech Compliance Platform</p>
        </div>

        <div className="login-card">
          <h2>Sign in</h2>
          <p>Use your <strong>@threebie.com</strong> Google account.</p>

          {oauthError && (
            <div className="login-error" role="alert">
              <strong>Sign-in didn't complete.</strong>
              <span>{decodeURIComponent(oauthError)}</span>
            </div>
          )}

          <button
            type="button"
            className="google-signin-btn"
            onClick={login}
            data-testid="google-signin-btn"
            disabled={loading}
          >
            {/* Inline Google "G" mark */}
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.96H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.333z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
            </svg>
            <span>Sign in with Google</span>
          </button>

          <div className="login-divider">
            <Link to="/trust-center" className="trust-link" data-testid="trust-center-link">
              <Globe size={16} />
              View Public Trust Center
            </Link>
          </div>
        </div>

        <p className="client-link">
          Client portal access? <Link to="/client-login">Click here</Link>
        </p>
      </div>
    </div>
  );
}

// ============ Dashboard Layout ============
function DashboardLayout({ children }) {
  const { user, logout, canRead } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', icon: BarChart3, label: 'Dashboard' },
    { path: '/controls', icon: ShieldCheck, label: 'Controls', resource: 'controls' },
    { path: '/policies', icon: FileText, label: 'Policies', resource: 'policies' },
    { path: '/findings', icon: AlertTriangle, label: 'Findings', resource: 'findings' },
    { path: '/pentest', icon: Bug, label: 'Security Scans', resource: 'pentest' },
    { path: '/accessibility', icon: Accessibility, label: 'Accessibility', resource: 'accessibility' },
    { path: '/monitors', icon: Zap, label: 'Monitors', resource: 'monitors' },
    { path: '/evidence', icon: Shield, label: 'Evidence', resource: 'evidence' },
    { path: '/caiq', icon: Globe, label: 'CAIQ Responses', resource: 'caiq' },
    { path: '/reports', icon: FileText, label: 'Reports', resource: 'reports' },
    { path: '/roster', icon: UserCog, label: 'Roster', resource: 'roster' },
    { path: '/audit-trail', icon: History, label: 'Audit Trail', resource: 'audit_trail' },
    { path: '/trust-center-preview', icon: Globe, label: 'Trust Center', resource: 'trust_center' },
    { path: '/trust-requests', icon: Mail, label: 'Trust Requests', resource: 'trust_center' },
    { path: '/clients', icon: Users, label: 'Client Mgmt', resource: 'clients' },
    { path: '/audit', icon: Package, label: 'Audit Export', resource: 'audit_export' },
    { path: '/users', icon: Lock, label: 'User Management', resource: 'user_management' },
  ];

  return (
    <div className="dashboard-layout light">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="mobile-menu-toggle"
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/threebie-logo.svg" alt="Threebie" className="sidebar-logo-img" />
          </div>
          <div className="sidebar-subtitle">Consumer Fintech Compliance</div>
        </div>

        <nav className="sidebar-nav">
          {navItems.filter(i => !i.resource || canRead(i.resource)).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <img
              src={user?.picture || 'https://via.placeholder.com/40'}
              alt={user?.name}
              className="user-avatar"
            />
            <div className="user-details">
              <p className="user-name">{user?.name}</p>
              <p className="user-email">{user?.email}</p>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {children}
      </main>
    </div>
  );
}

function DashboardOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);
  const [rosterSummary, setRosterSummary] = useState(null);
  const [userSummary, setUserSummary] = useState(null);

  // Map category icons
  const categoryIcons = {
    security: Shield,
    availability: Zap,
    processing_integrity: Settings,
    confidentiality: Lock,
    privacy: Eye,
  };

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [readinessRes, controlsRes, findingsRes, connectorsRes, statusRes, rosterRes] = await Promise.all([
          fetch(`${API_URL}/api/audit/readiness-score`, { credentials: 'include' }),
          fetch(`${API_URL}/api/controls/summary`, { credentials: 'include' }),
          fetch(`${API_URL}/api/findings/summary`, { credentials: 'include' }),
          fetch(`${API_URL}/api/connectors/health`, { credentials: 'include' }),
          fetch(`${API_URL}/api/system/status`, { credentials: 'include' }),
          fetch(`${API_URL}/api/roster`, { credentials: 'include' }),
        ]);

        const readiness = readinessRes.ok ? await readinessRes.json() : null;
        const controlsData = controlsRes.ok ? await controlsRes.json() : null;
        const findings = findingsRes.ok ? await findingsRes.json() : null;
        const connectors = connectorsRes.ok ? await connectorsRes.json() : null;
        const status = statusRes.ok ? await statusRes.json() : null;
        const roster = rosterRes.ok ? await rosterRes.json() : null;

        // Transform categories array to by_category object
        let controls = controlsData;
        if (controlsData?.categories && Array.isArray(controlsData.categories)) {
          controls = {
            ...controlsData,
            by_category: controlsData.categories.reduce((acc, cat) => {
              acc[cat.category] = cat;
              return acc;
            }, {}),
            compliance_pct: controlsData.overall_compliance_pct || 0,
            total: controlsData.total_controls || 51,
          };
        }

        setData({ readiness, controls, findings, connectors });
        setSystemStatus(status);
        if (roster?.roles) {
          const total = roster.roles.length;
          const assigned = roster.roles.filter(r => r.person_name && r.person_email).length;
          setRosterSummary({ total, assigned, unassigned: total - assigned });
        }

        // User management — best-effort (will 401/403 for plain Users)
        try {
          const uRes = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
          if (uRes.ok) {
            const u = await uRes.json();
            const list = u.users || [];
            setUserSummary({
              total: list.length,
              active: list.filter(x => x.is_active).length,
              admins: list.filter(x => x.role === 'admin').length,
              users: list.filter(x => x.role === 'user').length,
            });
          }
        } catch { /* ignore — user doesn't have access */ }
      } catch (error) {
        console.error('Dashboard fetch error:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  const readiness = data?.readiness;
  const controls = data?.controls;
  const findings = data?.findings;
  const connectors = data?.connectors;

  const overallPct = controls?.compliance_pct || controls?.overall_compliance_pct || 0;
  const totalControls = controls?.total || controls?.total_controls || 51;
  const openFindings = (findings?.by_status?.open || 0) + (findings?.by_status?.in_progress || 0);

  return (
    <div className="dashboard-page">
      {/* System Status Bar */}
      <div className="system-status-bar">
        <div className="status-item">
          <span className="status-label">Version</span>
          <span className="status-value">{systemStatus?.version || '1.0'}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Release Date</span>
          <span className="status-value">{systemStatus?.release_date || 'March 26, 2026'}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Database</span>
          <span className="status-value db-status">
            <span className={`status-indicator ${systemStatus?.database?.connected ? 'connected' : 'disconnected'}`} />
            {systemStatus?.database?.connected ? 'Connected' : 'Disconnected'}
            {systemStatus?.database?.connected && (
              <span className="db-name">({systemStatus?.database?.name})</span>
            )}
          </span>
        </div>
      </div>

      <div className="page-header">
        <div>
          <h1>Compliance Dashboard</h1>
          <p>SOC 2 Trust Services Criteria — Real-time Status</p>
        </div>
        <Badge color={readiness?.overall_score >= 70 ? "#059669" : "#d97706"}>
          Readiness: {readiness?.grade || 'N/A'} ({readiness?.overall_score || 0}%)
        </Badge>
      </div>

      {/* Stat Cards */}
      <div className="stats-grid" data-testid="metrics-grid">
        <StatCard 
          label="Total Controls" 
          value={totalControls} 
          sub="Across 5 TSC categories" 
        />
        <StatCard 
          label="Overall Compliance" 
          value={`${overallPct}%`} 
          color={overallPct >= 80 ? "#059669" : "#d97706"} 
        />
        <StatCard 
          label="Open Findings" 
          value={openFindings} 
          sub={findings?.open_critical ? `${findings.open_critical} Critical` : 'None critical'}
          color={openFindings > 0 ? "#d97706" : "#059669"} 
        />
        <StatCard 
          label="Active Policies" 
          value="15" 
          sub="Consumer fintech aligned" 
          color="#FF00BE" 
        />
      </div>

      {/* Trust Services Categories */}
      <h2 className="section-title">Trust Services Categories</h2>
      <div className="categories-grid">
        {controls?.by_category ? Object.entries(controls.by_category).map(([key, cat]) => {
          const meta = CATEGORIES[key] || { label: key, color: "#6b7280" };
          const Icon = categoryIcons[key] || Shield;
          const pct = cat.compliance_pct || 0;
          return (
            <Card key={key} className="category-card">
              <div className="ring-container">
                <ProgressRing pct={pct} size={100} stroke={8} color={meta.color} />
                <div className="ring-value" style={{ color: meta.color }}>{pct}%</div>
              </div>
              <div className="category-label">
                <Icon size={16} style={{ color: meta.color }} />
                {meta.label}
              </div>
              <div className="category-sub">{cat.passing || 0}/{cat.total || 0} passing</div>
            </Card>
          );
        }) : (
          Object.entries(CATEGORIES).map(([key, meta]) => {
            const Icon = categoryIcons[key] || Shield;
            return (
              <Card key={key} className="category-card">
                <div className="ring-container">
                  <ProgressRing pct={0} size={100} stroke={8} color={meta.color} />
                  <div className="ring-value" style={{ color: meta.color }}>0%</div>
                </div>
                <div className="category-label">
                  <Icon size={16} style={{ color: meta.color }} />
                  {meta.label}
                </div>
                <div className="category-sub">0/0 passing</div>
              </Card>
            );
          })
        )}
      </div>

      {/* Compliance Roster — quick access */}
      <Link to="/roster" className="roster-dashboard-card" data-testid="dashboard-roster-card">
        <div className="roster-dashboard-icon">
          <UserCog size={28} />
        </div>
        <div className="roster-dashboard-info">
          <h3>Compliance Roster</h3>
          <p>
            {rosterSummary
              ? `${rosterSummary.assigned} of ${rosterSummary.total} roles assigned${rosterSummary.unassigned > 0 ? ` — ${rosterSummary.unassigned} still need an owner` : ''}`
              : 'Manage role assignments referenced in policies'}
          </p>
        </div>
        <div className="roster-dashboard-action">
          <span>Open roster</span>
          <span className="arrow">→</span>
        </div>
      </Link>

      {/* User Management — Admins + Owners only */}
      {userSummary && (
        <Link to="/users" className="roster-dashboard-card users-dashboard-card" data-testid="dashboard-users-card">
          <div className="roster-dashboard-icon" style={{ background: 'rgba(139, 92, 246, 0.18)', color: '#a78bfa' }}>
            <Lock size={28} />
          </div>
          <div className="roster-dashboard-info">
            <h3>User Management</h3>
            <p>
              {userSummary.active} active user{userSummary.active === 1 ? '' : 's'}
              {userSummary.admins > 0 ? ` · ${userSummary.admins} admin${userSummary.admins === 1 ? '' : 's'}` : ''}
              {userSummary.users > 0 ? ` · ${userSummary.users} member${userSummary.users === 1 ? '' : 's'}` : ''}
              . Grant or revoke access per resource.
            </p>
          </div>
          <div className="roster-dashboard-action" style={{ color: '#a78bfa' }}>
            <span>Manage team</span>
            <span className="arrow">→</span>
          </div>
        </Link>
      )}

      {/* Security Scanning - Full Width */}
      <div className="security-scanning-section">
        <h2 className="section-title">Security Scanning Status</h2>
        <SecurityScanningWidget />
      </div>

      {/* Accessibility Status - Full Width */}
      <div className="accessibility-section">
        <h2 className="section-title">Accessibility Compliance</h2>
        <AccessibilityWidget />
      </div>

      {/* Two Column Layout */}
      <div className="two-column-grid">
        <div>
          {/* Environment Connectors */}
          <h2 className="section-title">Environment Connectors</h2>
          {connectors?.connectors ? Object.entries(connectors.connectors).map(([key, conn]) => (
            <Card key={key} className="connector-card">
              <div className="connector-info">
                <div className="connector-name">{conn.name || key}</div>
                <div className="connector-details">
                  {conn.connected ? JSON.stringify(conn.details || {}) : conn.error || 'Not configured'}
                </div>
              </div>
              <Badge color={conn.connected ? "#059669" : "#dc2626"}>
                {conn.connected ? "Connected" : "Disconnected"}
              </Badge>
            </Card>
          )) : (
            <>
              <Card className="connector-card">
                <div className="connector-info">
                  <div className="connector-name">New Threebie (FastAPI/MongoDB)</div>
                  <div className="connector-details">Not configured</div>
                </div>
                <Badge color="#6b7280">Pending</Badge>
              </Card>
              <Card className="connector-card">
                <div className="connector-info">
                  <div className="connector-name">Threebie Hosting</div>
                  <div className="connector-details">Awaiting credentials</div>
                </div>
                <Badge color="#6b7280">Pending</Badge>
              </Card>
            </>
          )}

          {/* Open Findings */}
          <h2 className="section-title" style={{ marginTop: 24 }}>Open Findings</h2>
          {findings?.by_severity && Object.keys(findings.by_severity).length > 0 ? (
            <Card className="findings-summary">
              {Object.entries(findings.by_severity).map(([sev, count]) => (
                <div key={sev} className="finding-row">
                  <Badge color={STATUS_COLORS[sev] || "#6b7280"}>{sev}</Badge>
                  <span>{count} findings</span>
                </div>
              ))}
            </Card>
          ) : (
            <Card className="empty-state">
              <Activity size={32} color="#059669" />
              <p>No open findings</p>
            </Card>
          )}
        </div>

        <div>
          {/* Readiness Breakdown */}
          <h2 className="section-title">Readiness Breakdown</h2>
          {readiness?.breakdown ? Object.entries(readiness.breakdown).map(([key, item]) => (
            <div key={key} className="readiness-item">
              <div className="readiness-header">
                <span>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                <span style={{ color: item.score >= 70 ? "#059669" : item.score >= 40 ? "#d97706" : "#dc2626" }}>
                  {item.score}% ({item.weight}% weight)
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${item.score}%`,
                    background: item.score >= 70 ? "#059669" : item.score >= 40 ? "#d97706" : "#dc2626"
                  }} 
                />
              </div>
            </div>
          )) : (
            <Card className="empty-state">
              <Activity size={32} color="#6b7280" />
              <p>Readiness data loading...</p>
            </Card>
          )}

          {/* Recommendations */}
          <h2 className="section-title" style={{ marginTop: 24 }}>Recommendations</h2>
          <Card>
            {readiness?.recommendations?.map((rec, i) => (
              <div key={i} className="recommendation">
                <span className="rec-arrow">→</span>
                {rec}
              </div>
            )) || (
              <p className="empty-text">No recommendations at this time.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ============ App Router ============
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/client-login" element={<ClientLoginPage />} />
      <Route path="/client-portal" element={<ClientPortal />} />
      <Route path="/trust-center" element={<TrustCenterPage />} />
      <Route path="/security-overview" element={<SecurityOverviewPage />} />
      {/* Public policies — no auth, indexable. Mounted under /legal to
          avoid colliding with the internal staff /policies dashboard. */}
      <Route path="/legal" element={<PublicPoliciesListPage />} />
      <Route path="/legal/:policyId" element={<PublicPolicyDetailPage />} />
      <Route path="/privacy" element={<PublicPolicyDetailPage />} />
      <Route path="/privacy-policy" element={<PublicPolicyDetailPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout><DashboardOverview /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/controls"
        element={
          <ProtectedRoute>
            <DashboardLayout><ControlsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/policies"
        element={
          <ProtectedRoute>
            <DashboardLayout><PoliciesPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roster"
        element={
          <ProtectedRoute>
            <DashboardLayout><RosterPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <DashboardLayout><UserManagementPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trust-requests"
        element={
          <ProtectedRoute>
            <DashboardLayout><TrustRequestsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/findings"
        element={
          <ProtectedRoute>
            <DashboardLayout><FindingsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/monitors"
        element={
          <ProtectedRoute>
            <DashboardLayout><MonitorsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pentest"
        element={
          <ProtectedRoute>
            <DashboardLayout><PentestPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accessibility"
        element={
          <ProtectedRoute>
            <DashboardLayout><AccessibilityPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evidence"
        element={
          <ProtectedRoute>
            <DashboardLayout><EvidencePage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/caiq"
        element={
          <ProtectedRoute>
            <DashboardLayout><CAIQPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <DashboardLayout><ReportsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trust-center-preview"
        element={
          <ProtectedRoute>
            <DashboardLayout><TrustCenterPreview /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <DashboardLayout><ClientsPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <DashboardLayout><AuditPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-trail"
        element={
          <ProtectedRoute>
            <DashboardLayout><AuditTrailPage /></DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/trust-center" replace />} />
      <Route path="*" element={<Navigate to="/trust-center" replace />} />
    </Routes>
  );
}

// ============ Main App ============
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ClientAuthProvider>
          <AppRouter />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#1a1d27',
                color: '#e4e6ee',
                border: '1px solid #2a2d3a',
              },
            }}
          />
        </ClientAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
